import { useState, useEffect } from "react";
import { Facebook, Instagram, Youtube, Mail, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { cn } from "@/lib/utils";
import defaultLogo from "@/assets/footer-logo-optimized.png";

interface Game {
  id: string;
  title: string;
}

const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const getGameSlug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const Footer = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const { settings } = useSiteSettings();

  useEffect(() => {
    const fetchGames = async () => {
      const { data } = await supabase
        .from("games")
        .select("id, title")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });

      if (data) setGames(data);
    };
    fetchGames();
  }, []);

  const allSocialLinks = [
    { icon: Facebook, href: settings.social_facebook, label: "Facebook" },
    { icon: Instagram, href: settings.social_instagram, label: "Instagram" },
    { icon: XIcon, href: settings.social_x, label: "X" },
    { icon: Youtube, href: settings.social_youtube, label: "YouTube" },
    { icon: Mail, href: settings.social_email ? `mailto:${settings.social_email}` : "", label: "Email" },
  ];

  const socialLinks = allSocialLinks.filter((link) => link.href && link.href !== "" && link.href !== "mailto:");

  return (
    <footer className="bg-[#000000] overflow-x-hidden">
      <div className="px-8">
        {/* Main Footer Content */}
        <div className="pt-16 pb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-4">
              <img
                src={settings.site_footer_logo || defaultLogo}
                alt="Rogue Skull Games"
                className="logo h-20 w-auto"
                width={250}
                height={45}
                loading="lazy"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs">
              We're committed to crafting captivating tabletop experiences that unite players. Your support fuels our
              passion, and together, we'll embark on countless gaming adventures.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={`Follow us on ${social.label}`}
                  className="w-10 h-10 rounded-full border border-border/20 flex items-center justify-center text-dark-foreground/70 hover:text-foreground hover:border-foreground/30 transition-all duration-300"
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Games Links */}
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setGamesOpen((open) => !open)}
              aria-expanded={gamesOpen}
              className="w-full flex items-center justify-between text-foreground font-display font-bold text-lg uppercase tracking-wider mb-5 md:pointer-events-none md:mb-5"
            >
              Games
              <ChevronDown
                className={cn("w-5 h-5 transition-transform duration-300 md:hidden", gamesOpen && "rotate-180")}
              />
            </button>
            <nav
              className={cn("flex-col gap-3 text-dark-foreground/70 md:flex md:gap-3", gamesOpen ? "flex" : "hidden")}
            >
              {games.map((game) => (
                <Link key={game.id} to={`/games/${getGameSlug(game.title)}`} className="footer-link">
                  {game.title}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company Links */}
          <div className="lg:col-span-2">
            <button
              type="button"
              onClick={() => setCompanyOpen((open) => !open)}
              aria-expanded={companyOpen}
              className="w-full flex items-center justify-between text-foreground font-display font-bold text-lg uppercase tracking-wider mb-5 md:pointer-events-none md:mb-5"
            >
              Company
              <ChevronDown
                className={cn("w-5 h-5 transition-transform duration-300 md:hidden", companyOpen && "rotate-180")}
              />
            </button>
            <nav
              className={cn("flex-col gap-3 text-dark-foreground/70 md:flex md:gap-3", companyOpen ? "flex" : "hidden")}
            >
              {settings.news_enabled !== "false" && (
                <Link to="/news" className="footer-link">
                  News
                </Link>
              )}
              <Link to="/about" className="footer-link">
                About
              </Link>
              <Link to="/contact" className="footer-link">
                Contact
              </Link>
              <Link to="/ai-usage" className="footer-link">
                Use of AI
              </Link>
              <Link to="/return-policy" className="footer-link">
                Return Policy
              </Link>
              <Link to="/privacy-policy" className="footer-link">
                Privacy Policy
              </Link>
              <Link to="/terms-of-use" className="footer-link">
                Terms of Use
              </Link>
            </nav>
          </div>

          {/* Contact Us */}
          <div className="lg:col-span-4">
            <h4 className="text-foreground font-display font-bold text-lg uppercase tracking-wider mb-5">Contact Us</h4>
            <p className="text-muted-foreground text-sm leading-relaxed mb-5 max-w-xs">
              Questions, playtesting, partnerships or just want to say ahoy? We'd love to hear from you.
            </p>
            <a
              href="mailto:rogueskullgames@gmail.com"
              className="btn-outline-light group inline-flex w-full sm:w-auto justify-center"
            >
              Get in Touch
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="py-6 border-t border-border/60 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-xs">
            © {new Date().getFullYear()} Rogue Skull Games LLC. All rights reserved.
          </p>
          <p className="text-muted-foreground text-xs">Website by Kyle Lundy</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
